/**
 * Turning "I want June" into the two bounds the order endpoints expect.
 *
 * The API treats `startDate`/`endDate` as an INCLUSIVE wall-clock range against
 * `orders.createdAt`, with no timezone conversion in either direction (see the
 * API's util/orderDateRange.js). So the only thing that can go wrong here is
 * naming the wrong instant — which is exactly what the bare `datetime-local`
 * inputs did: picking a day in "الى" fills the time with 00:00, and "through
 * 30/06 00:00" silently drops the whole 30th. On June 2026 that was 241 orders
 * missing from the count, the totals and any statement built from the view.
 *
 * Everything here produces `YYYY-MM-DDTHH:mm`, the format the inputs already
 * carry in the URL, so nothing downstream has to change.
 */
import { formatMonth, WEEKDAYS, WEEKDAY_LABELS } from "./dateFormat";

/** "01" to "12": the month names of the date picker. */
const MONTH_NUMBERS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));

const pad = (value) => String(value).padStart(2, "0");

/** "2026-06-30" for a Date, in LOCAL fields — never toISOString, which is UTC. */
const toLocalDate = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/**
 * The full inclusive range for one calendar month.
 *
 * @param {string} month - "YYYY-MM", the value an `<input type="month">` gives.
 * @returns {{startDate: string, endDate: string}|null}
 */
export const monthRange = (month) => {
    const match = /^(\d{4})-(\d{2})$/.exec(String(month || ""));
    if (!match) return null;

    const [, year, monthNumber] = match;
    // Shape alone is not enough: "2026-13" matches the pattern, and
    // `new Date(2026, 13, 0)` rolls forward into 2027 rather than complaining,
    // so an unreadable month would come back as a confident wrong range.
    if (Number(monthNumber) < 1 || Number(monthNumber) > 12) return null;

    // Day 0 of the NEXT month is the last day of this one, which is how this
    // avoids a table of month lengths and a February special case.
    const lastDay = new Date(Number(year), Number(monthNumber), 0);

    return {
        startDate: `${year}-${monthNumber}-01T00:00`,
        endDate: `${toLocalDate(lastDay)}T23:59`,
    };
};

/**
 * Which month a start/end pair currently represents, or "" when the range is
 * anything other than exactly one whole month. Lets the picker show what is
 * selected without claiming a hand-typed partial range is a month.
 *
 * @param {string|null} startDate
 * @param {string|null} endDate
 * @returns {string} "YYYY-MM" or ""
 */
export const monthOf = (startDate, endDate) => {
    const match = /^(\d{4})-(\d{2})/.exec(String(startDate || ""));
    if (!match) return "";

    const candidate = `${match[1]}-${match[2]}`;
    const range = monthRange(candidate);

    return range && range.startDate === startDate && range.endDate === endDate ? candidate : "";
};

/** "YYYY-MM" for the month containing `date` (default: today). */
export const currentMonth = (date = new Date()) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;

/**
 * The last `count` months, newest first, as {value, label} for a select.
 *
 * A list rather than a picker because a month IS a short list — the orders go
 * back to January 2025 — and because it then looks and behaves like every other
 * filter on the bar instead of opening a browser-chrome popup.
 *
 * @param {number} [count=24]
 * @param {Date} [from=new Date()]
 */
export const recentMonths = (count = 24, from = new Date()) => {
    const months = [];

    for (let back = 0; back < count; back += 1) {
        // Day 1 of the target month, built from local fields so no date near a
        // month edge can roll into its neighbour.
        const date = new Date(from.getFullYear(), from.getMonth() - back, 1);
        const value = `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
        months.push({ value, label: formatMonth(value) });
    }

    return months;
};

/**
 * The time a `datetime-local` field should carry when a DAY is picked.
 *
 * Left alone, the browser fills in the current clock time the moment a date is
 * chosen, so picking 01/05 → 31/05 at 14:20 in the afternoon quietly asks for
 * "14:20 on the 1st through 14:20 on the 31st" — it drops the morning of the
 * first day and most of the last. Nothing on screen says so; the count simply
 * comes back smaller.
 *
 * Only snaps when the DAY actually changed. Someone who typed 14:30 into الى on
 * purpose is narrowing to an afternoon and must keep it — stomping a
 * deliberately-set clock would be the same class of surprise in the other
 * direction.
 *
 * @param {string} nextValue - "YYYY-MM-DDTHH:mm" straight from the input.
 * @param {string|null} previousValue - What the field held before this change.
 * @param {"start"|"end"} edge
 * @returns {string}
 */
export const withDefaultTime = (nextValue, previousValue, edge) => {
    const next = String(nextValue || "");
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(next)) return next;

    const nextDay = next.slice(0, 10);
    const previousDay = String(previousValue || "").slice(0, 10);

    if (nextDay === previousDay) return next;

    return `${nextDay}T${edge === "end" ? "23:59" : "00:00"}`;
};

/**
 * "YYYY-MM-DDTHH:mm" → Date, built from LOCAL fields.
 *
 * Never `new Date(string)`: a bare "2026-05-01" parses as midnight UTC, which
 * is the exact conversion that cost the API a month's first hours before
 * util/orderDateRange.js took it away. The same rule applies on this side.
 *
 * @returns {Date|null}
 */
export const parseLocalDateTime = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(String(value || ""));
    if (!match) return null;

    const [, year, month, day, hour = "00", minute = "00"] = match;
    return new Date(+year, +month - 1, +day, +hour, +minute, 0, 0);
};

/** Date → "YYYY-MM-DDTHH:mm", local fields — never toISOString, which is UTC. */
export const formatLocalDateTime = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
};

/**
 * PrimeReact locale payload for its date picker.
 *
 * The weekday names come from the one list in utils/dateFormat.js. The months
 * are numbers, as everywhere in the dashboard: the popup's title reads
 * «10 2026». `am` and `pm` are the picker's own keys for the 12-hour clock.
 */
export const AR_CALENDAR_LOCALE = {
    firstDayOfWeek: 6,
    dayNames: WEEKDAYS.map((day) => WEEKDAY_LABELS[day]),
    dayNamesShort: ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"],
    dayNamesMin: ["ح", "ن", "ث", "ر", "خ", "ج", "س"],
    monthNames: MONTH_NUMBERS,
    monthNamesShort: MONTH_NUMBERS,
    am: "AM",
    pm: "PM",
    today: "اليوم",
    // PrimeReact labels the button bar with `now` instead of `today` whenever
    // the picker shows a time, so both keys have to be here or one of them
    // falls back to English.
    now: "الآن",
    clear: "مسح",
    dateFormat: "dd/mm/yy",
};

/**
 * The date a picker should OPEN on when its field is still empty.
 *
 * Today, at the time that bound means: 00:00 for a start, 23:59 for an end.
 * Without it the picker seeds its clock from the current time, so opening the
 * من field at 21:47 showed 21:47 — and the operator saw a time they had not
 * chosen, which then jumped to 00:00 as soon as they picked a day. Same
 * destination, but it should read that way from the moment it opens.
 *
 * @param {"start"|"end"} edge
 * @param {Date} [from=new Date()]
 * @returns {Date}
 */
export const defaultBoundDate = (edge, from = new Date()) => {
    const end = edge === "end";
    return new Date(
        from.getFullYear(),
        from.getMonth(),
        from.getDate(),
        end ? 23 : 0,
        end ? 59 : 0,
    );
};

/** Date → "YYYY-MM-DD", local fields. For fields that store a day, not a time. */
export const formatLocalDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
